import { useMemo } from 'react';

import { EMBRYO_METHOD_OPTIONS, FRESH_FROZEN, SEX_EMBRYO } from '@/constants';
import type {
  ColDef as TypedColDef,
  ValueFormatterParams as TypedValueFormatterParams,
} from '@/grid/types';
import { dateOnlyFormatter } from '@/grid/value-formatters/dateOnlyFormatter';
import { embryoNameFormatter } from '@/grid/value-formatters/embryoNameFormatter';
import { utcDateFormatter } from '@/grid/value-formatters/utcInstantFormatter';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  clinicRanchOptions,
  exportOptions,
  implantStatusOptions,
  implantTypeOptions,
  pcResOptions,
} from '@/pages/embryo-app/components/EmbryoForm/form-options';
import {
  MAIN_GRID_FIELDS,
  useEmbryoFieldsEnabled,
} from '@/pages/embryo-app/components/EmbryoForm/full-inventory';
import {
  EMBRYO_STATUS_MAP,
  EMBRYO_STATUS_MAP_WITH_MISSING,
} from '@/pages/fresh-straw/constants';

type EmbryoInventoryRow = Record<string, unknown>;
type ValueFormatterParams = TypedValueFormatterParams<EmbryoInventoryRow>;
type EmbryoInventoryColumn = TypedColDef<EmbryoInventoryRow>;
type CodeOption = { label: string; value: number };

const mainGridFields = new Set<string>(MAIN_GRID_FIELDS);

const baseFilterParams = { buttons: ['apply', 'clear'], closeOnApply: true };

function textColumn(
  field: string,
  headerName: string,
  width: number,
): EmbryoInventoryColumn {
  return {
    field,
    headerName,
    width,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { ...baseFilterParams },
    sortable: true,
  };
}

function numberColumn(
  field: string,
  headerName: string,
  width: number,
): EmbryoInventoryColumn {
  return {
    field,
    headerName,
    width,
    filter: 'agNumberColumnFilter',
    wrapHeaderText: true,
    filterParams: { ...baseFilterParams },
    sortable: true,
  };
}

function dateColumn(
  field: string,
  headerName: string,
  width: number,
): EmbryoInventoryColumn {
  return {
    field,
    headerName,
    width,
    filter: 'agDateColumnFilter',
    wrapHeaderText: true,
    filterParams: { ...baseFilterParams },
    valueFormatter: dateOnlyFormatter,
    sortable: true,
  };
}

function codeColumn(
  field: string,
  headerName: string,
  width: number,
  options: readonly CodeOption[],
): EmbryoInventoryColumn {
  const format = (params: ValueFormatterParams) => {
    if (params.value === null || params.value === undefined) return '';
    return options.find((opt) => opt.value === params.value)?.label ?? '';
  };

  return {
    field,
    headerName,
    width,
    filter: 'agSetColumnFilter',
    wrapHeaderText: true,
    filterParams: {
      values: options.map((opt) => opt.value),
      valueFormatter: format,
      ...baseFilterParams,
    },
    valueFormatter: format,
    sortable: true,
  };
}

export function useEmbryoInventoryColumns() {
  const addRemoveEmbryoFields = useEmbryoFieldsEnabled();
  const finalEmbryoDatapoints = useFeatureFlag('Final_Embryo_Datapoints');

  const isPsiColumnEnabled = useFeatureFlag('Embryo_Psi_Column');
  const isCollectionMethodRename = useFeatureFlag(
    'Embryo_Collection_Method_Rename',
  );
  const embryoMissingStatus = useFeatureFlag('Embryo_Missing_Status');

  return useMemo<TypedColDef<EmbryoInventoryRow>[]>(() => {
    const statusMap = embryoMissingStatus
      ? EMBRYO_STATUS_MAP_WITH_MISSING
      : EMBRYO_STATUS_MAP;
    const statusFilterValues = Object.keys(statusMap).map(Number);

    let cols: TypedColDef<EmbryoInventoryRow>[] = [
      {
        headerName: '',
        width: 50,
        checkboxSelection: true,
        suppressMovable: true,
        lockPosition: true,
        sortable: true,
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 120,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
        valueFormatter: embryoNameFormatter,
      },
      {
        field: 'creation',
        headerName: finalEmbryoDatapoints ? 'Embryo Creation' : 'Created On',
        width: 110,
        filter: 'agDateColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        valueFormatter: dateOnlyFormatter,
        sortable: true,
      },
      {
        field: 'siteName',
        headerName: 'Site',
        width: 120,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        valueGetter: (params) => {
          return params.data?.siteName ?? params.data?.freezeSiteName;
        },
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      {
        field: 'method',
        headerName: isCollectionMethodRename
          ? 'Collection Method'
          : 'Freeze Method',
        width: 110,
        filter: 'agSetColumnFilter',
        wrapHeaderText: true,
        filterParams: {
          values: EMBRYO_METHOD_OPTIONS.map((opt) => opt.value),
          valueFormatter: (params: ValueFormatterParams) => {
            return (
              EMBRYO_METHOD_OPTIONS.find((opt) => opt.value === params.value)
                ?.label ?? ''
            );
          },
          buttons: ['apply', 'clear'],
          closeOnApply: true,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return (
            EMBRYO_METHOD_OPTIONS.find((opt) => opt.value === params.value)
              ?.label ?? ''
          );
        },
        sortable: true,
      },
      {
        field: 'freshFrozen',
        headerName: 'Fresh/Frozen',
        width: 110,
        filter: 'agSetColumnFilter',
        wrapHeaderText: true,
        filterParams: {
          values: [FRESH_FROZEN.Fresh, FRESH_FROZEN.Frozen],
          valueFormatter: (params: ValueFormatterParams) => {
            if (params.value === FRESH_FROZEN.Fresh) return 'Fresh';
            if (params.value === FRESH_FROZEN.Frozen) return 'Frozen';
            return '';
          },
          buttons: ['apply', 'clear'],
          closeOnApply: true,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === null || params.value === undefined) return '';
          if (params.value === FRESH_FROZEN.Fresh) return 'Fresh';
          if (params.value === FRESH_FROZEN.Frozen) return 'Frozen';
          return '';
        },
        sortable: true,
      },
      {
        field: 'donorReg',
        headerName: 'Reg Number',
        width: 110,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      {
        field: 'donorName',
        headerName: 'Donor',
        width: 120,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      {
        field: 'donorBreedName',
        headerName: 'Donor Breed',
        width: 110,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      {
        field: 'sireName',
        headerName: 'Sire',
        width: 120,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      {
        field: 'sireBreedName',
        headerName: 'Sire Breed',
        width: 110,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      {
        field: 'naabCode',
        headerName: 'Sire Naab',
        width: 110,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      ...(!addRemoveEmbryoFields
        ? ([
            {
              field: 'psi',
              headerName: 'PA PSI',
              width: 90,
              filter: 'agNumberColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
            },
          ] as TypedColDef<EmbryoInventoryRow>[])
        : []),

      {
        field: 'ownerName',
        headerName: 'Embryo Owner',
        width: 130,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      ...(!addRemoveEmbryoFields
        ? ([
            {
              field: 'billingOwnerName',
              headerName: 'Billing Owner',
              width: 130,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
            },
            {
              field: 'implantBillingAccountName',
              headerName: 'Implant Billing Owner',
              width: 170,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
            },
          ] as TypedColDef<EmbryoInventoryRow>[])
        : []),

      {
        field: 'soldTo',
        headerName: 'Sold To',
        width: 120,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      {
        field: 'cane',
        headerName: 'Cane',
        width: 100,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      ...(!addRemoveEmbryoFields
        ? ([
            {
              field: 'shipmentNumber',
              headerName: 'Shipment Number',
              width: 130,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
            },
          ] as TypedColDef<EmbryoInventoryRow>[])
        : []),

      {
        field: 'semenColCode',
        headerName: 'Semen Collection Code',
        width: 150,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      {
        field: 'receivedOn',
        headerName: 'Received Date',
        width: 120,
        filter: 'agDateColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        valueFormatter: dateOnlyFormatter,
        sortable: true,
      },
      ...(!addRemoveEmbryoFields
        ? ([
            {
              field: 'fertSiteName',
              headerName: 'Fert Site',
              width: 110,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
            },
          ] as TypedColDef<EmbryoInventoryRow>[])
        : []),

      {
        field: 'freezeTime',
        headerName: 'Freeze Time',
        width: 110,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      {
        field: 'recipRegNum',
        headerName: 'Recip',
        width: 120,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      {
        field: 'stage',
        headerName: 'Stage',
        width: 80,
        filter: 'agNumberColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      {
        field: 'grade',
        headerName: 'Grade',
        width: 80,
        filter: 'agNumberColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      {
        field: 'strawNumber',
        headerName: 'Straw',
        width: 90,
        filter: 'agNumberColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      {
        field: 'tt',
        headerName: 'TT',
        width: 120,
        filter: 'agSetColumnFilter',
        wrapHeaderText: true,
        filterParams: {
          values: [true, false],
          valueFormatter: (params: ValueFormatterParams) => {
            if (params.value === true) return 'Yes';
            if (params.value === false) return 'No';
            return '';
          },
          buttons: ['apply', 'clear'],
          closeOnApply: true,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === null || params.value === undefined) return '';
          return params.value ? 'Yes' : 'No';
        },
        sortable: true,
      },
      {
        field: 'sex',
        headerName: 'Sex',
        width: 80,
        filter: 'agSetColumnFilter',
        wrapHeaderText: true,
        filterParams: {
          values: [100000000, 100000001, 100000002],
          valueFormatter: (params: ValueFormatterParams) => {
            const sexMap: { [key: number]: string } = {
              [SEX_EMBRYO.Female]: 'Female',
              [SEX_EMBRYO.Male]: 'Male',
              [SEX_EMBRYO.Unknown]: 'Unknown',
            };
            return sexMap[params.value as number] || '';
          },
          buttons: ['apply', 'clear'],
          closeOnApply: true,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === null || params.value === undefined) return '';
          const sexMap: { [key: number]: string } = {
            [SEX_EMBRYO.Female]: 'Female',
            [SEX_EMBRYO.Male]: 'Male',
            [SEX_EMBRYO.Unknown]: 'Unknown',
          };
          return sexMap[params.value as number] || '';
        },
        sortable: true,
      },
      {
        field: 'storageLocationName',
        headerName: 'Storage Location',
        width: 130,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        valueGetter: (params) => {
          return (
            params.data?.storageLocationName ??
            params.data?.freezeStorageLocationName
          );
        },
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      ...(!addRemoveEmbryoFields
        ? ([
            {
              field: 'shipToFbd',
              headerName: 'Ship To (FBD)',
              width: 120,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
            },
          ] as TypedColDef<EmbryoInventoryRow>[])
        : []),

      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        filter: 'agSetColumnFilter',
        wrapHeaderText: true,
        valueGetter: (params) => {
          return params.data?.status;
        },
        filterParams: {
          values: statusFilterValues,
          valueFormatter: (params: ValueFormatterParams) => {
            return statusMap[params.value as keyof typeof statusMap] || '';
          },
          buttons: ['apply', 'clear'],
          closeOnApply: true,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === null || params.value === undefined) return '';
          return statusMap[params.value as keyof typeof statusMap] || '';
        },
        sortable: true,
      },
      {
        field: 'stateCode',
        headerName: 'Active',
        width: 100,
        filter: 'agSetColumnFilter',
        wrapHeaderText: true,
        filterParams: {
          values: [0, 1],
          valueFormatter: (params: ValueFormatterParams) => {
            if (params.value === 0) return 'Active';
            if (params.value === 1) return 'Inactive';
            return '';
          },
          buttons: ['apply', 'clear'],
          closeOnApply: true,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === null || params.value === undefined) return '';
          return params.value === 0 ? 'Active' : 'Inactive';
        },
        sortable: true,
      },
      {
        field: 'recoveryFirmCode',
        headerName: 'Recovery Firm Code',
        width: 140,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        sortable: true,
      },
      ...(addRemoveEmbryoFields
        ? ([
            {
              field: 'psi',
              headerName: 'PA PSI',
              width: 90,
              filter: 'agNumberColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
            },
            {
              field: 'ownerCode',
              headerName: 'Ranch Id',
              width: 110,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
            },
            {
              field: 'cellId',
              headerName: 'Cell Id',
              width: 100,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'crackedZona',
              headerName: 'Cracked Zona',
              width: 110,
              filter: 'agSetColumnFilter',
              wrapHeaderText: true,
              filterParams: {
                values: [true, false],
                valueFormatter: (params: ValueFormatterParams) => {
                  if (params.value === true) return 'Yes';
                  if (params.value === false) return 'No';
                  return '';
                },
                buttons: ['apply', 'clear'],
                closeOnApply: true,
              },
              valueFormatter: (params: ValueFormatterParams) => {
                if (params.value === null || params.value === undefined)
                  return '';
                return params.value ? 'Yes' : 'No';
              },
              sortable: true,
              hide: true,
            },
            {
              field: 'dairyCompEmbryoId',
              headerName: 'Dairy Comp Embryo Id',
              width: 130,
              filter: 'agNumberColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'embryoPa',
              headerName: 'Embryo PA',
              width: 100,
              filter: 'agNumberColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'embryoStagingId',
              headerName: 'Embryo Staging Id',
              width: 130,
              filter: 'agNumberColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'embTagNumber',
              headerName: 'Embryo Tag',
              width: 100,
              filter: 'agNumberColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'freezeComment',
              headerName: 'Freeze Comment',
              width: 150,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'freezeTechName',
              headerName: 'Freeze Tech',
              width: 120,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'freezeMethodName',
              headerName: 'Freeze Method Name',
              width: 140,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'group',
              headerName: 'Group',
              width: 80,
              filter: 'agNumberColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'lastAct',
              headerName: 'Last Activity',
              width: 120,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'recId',
              headerName: 'Record Id',
              width: 100,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'recoveryFirmName',
              headerName: 'Recovery Firm Name',
              width: 150,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'semenName',
              headerName: 'Semen',
              width: 120,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'strawDisclaimer',
              headerName: 'Straw Disclaimer',
              width: 140,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'togId',
              headerName: 'TOG ID',
              width: 80,
              filter: 'agNumberColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'approvalNumberName',
              headerName: 'Approval Number',
              width: 140,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'scSyDate',
              headerName: 'SY Date',
              width: 110,
              filter: 'agDateColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              valueFormatter: dateOnlyFormatter,
              sortable: true,
              hide: true,
            },
            {
              field: 'tempEmbryoId',
              headerName: 'Temp Embryo Id',
              width: 130,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'modifiedByName',
              headerName: 'Modified By',
              width: 130,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'modifiedOn',
              headerName: 'Modified On',
              width: 110,
              filter: 'agDateColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              valueFormatter: utcDateFormatter,
              sortable: true,
              hide: true,
            },
            {
              field: 'freshStrawNo',
              headerName: 'Fresh Straw Number',
              width: 120,
              filter: 'agNumberColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'quantity',
              headerName: 'Quantity',
              width: 90,
              filter: 'agNumberColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
            {
              field: 'transferDate',
              headerName: 'Transaction Date',
              width: 120,
              filter: 'agDateColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              valueFormatter: dateOnlyFormatter,
              sortable: true,
              hide: true,
            },
            {
              field: 'cellLineName',
              headerName: 'Cell Line',
              width: 120,
              filter: 'agTextColumnFilter',
              wrapHeaderText: true,
              filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
              sortable: true,
              hide: true,
            },
          ] as TypedColDef<EmbryoInventoryRow>[])
        : []),
      ...(finalEmbryoDatapoints
        ? [
            textColumn('billingOwnerName', 'Billing Owner', 130),
            textColumn('shipToFbd', 'Ship To (FBD)', 120),
            numberColumn('donorNetMerit', 'Donor Net Merit $', 120),
            numberColumn('donorTpi', 'Donor TPI', 100),
            numberColumn('holsteinPtanetMerit', 'PA Net Merit $', 120),
            numberColumn('tpi', 'PA TPI', 90),
            textColumn('freezer', 'Freezer', 110),
            numberColumn('frozenStage', 'Frozen Stage', 100),
            numberColumn('frozenGrade', 'Frozen Grade', 100),
            codeColumn('export', 'Export', 110, exportOptions),
            textColumn('exportTest1', 'Export Test 1', 110),
            textColumn('exportTest2', 'Export Test 2', 110),
            textColumn('exportTest3', 'Export Test 3', 110),
            dateColumn('pc1Date', 'PC 1 Date', 110),
            codeColumn('pc1Res', 'PC 1 Result', 110, pcResOptions),
            dateColumn('pc2Date', 'PC 2 Date', 110),
            codeColumn('pc2Res', 'PC 2 Result', 110, pcResOptions),
            dateColumn('pc3Date', 'PC 3 Date', 110),
            codeColumn('pc3Res', 'PC 3 Result', 110, pcResOptions),
            dateColumn('implantDate', 'Implant Date', 110),
            codeColumn(
              'implantStatusCode',
              'Implant Status',
              120,
              implantStatusOptions,
            ),
            codeColumn('calfType', 'Implant Type', 140, implantTypeOptions),
            codeColumn(
              'clinicRanchHaulIn',
              'Clinic/Ranch/Haul-In',
              130,
              clinicRanchOptions,
            ),
            textColumn('recipientTag', 'Recip', 110),
            textColumn('recipOwnerName', 'Recip Owner', 130),
            textColumn('clientRecipient', 'Recip Tag (For Client)', 140),
            textColumn(
              'transferFarmLocationName',
              'Transfer Farm Location',
              150,
            ),
            textColumn('programName', 'Program Name', 130),
            dateColumn('createdOn', 'Created On', 110),
          ]
        : []),
    ];

    if (!isPsiColumnEnabled) {
      cols = cols.filter((col) => col.field !== 'psi');
    }

    if (finalEmbryoDatapoints) {
      cols = cols
        .filter((col) => !col.field || mainGridFields.has(col.field as string))
        .map((col) => (col.hide ? { ...col, hide: false } : col));
    }

    return cols;
  }, [
    addRemoveEmbryoFields,
    finalEmbryoDatapoints,
    isPsiColumnEnabled,
    isCollectionMethodRename,
    embryoMissingStatus,
  ]);
}
